import { useState, useEffect, useContext } from "react";
import placeholder from "../inc/placeholder.jpg";
import { SiteContext } from "../contexts/SiteContext";
import { ViewContext } from "../contexts/ViewContext";
import {
	Paper,
	Hidden,
	Typography,
	IconButton,
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	makeStyles,
	Fade,
	Tooltip,
	Grid,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewGridIcon from "@material-ui/icons/ViewModule";

const useStyles = makeStyles((theme) => ({
	paper: {
		padding: "1rem",
		marginBottom: "1rem",
		position: 'relative'
	},
	media: { paddingBottom: "100%" },
	horizCard: {
		marginBottom: "1rem",
	},
	link: {
		color: "black",
		textDecoration: "none",
	},
	horizCardActionArea: {
		display: "flex",
		justifyContent: "start",
	},
	horizMedia: {
		paddingBottom: "20%",
		paddingRight: "20%",
	},
	horizCardContent: {},
}));

//---------------
// MAIN COMPONENT
//---------------

function Entries({ unit, theme, region }) {
	const classes = useStyles();
	const [entries, setEntries] = useState();
	const [loading, setLoading] = useState(true);
	const { entriesView, setEntriesView } = useContext(ViewContext);

	useEffect(() => {
		const fetchData = async () => {
			let url;
			if (unit) {
				url = `${process.env.REACT_APP_API_URL}/entries?sort=title&unit=${unit._id}`;
			} else if (theme) {
				url = `${process.env.REACT_APP_API_URL}/entries?sort=title&themes[in]=${theme._id}`;
			} else if (region) {
                url = `${process.env.REACT_APP_API_URL}/entries?sort=title&regions[in]=${region._id}`
            }
			const res = await fetch(url, { credentials: "include" });
			let allEntries = await res.json();
			if (allEntries.success) {
				// Filter out unpublished entries
				setEntries(allEntries.data.filter((x) => x.published));
			} else {
				console.log(allEntries.error);
			}
		};
		fetchData();
		setLoading(false);
	}, [unit, theme]);

	if (loading || !entriesView) {
		return "Loading...";
	}

	return (
		<>
			{entries && entries.length > 0 && (
				<Fade in={!loading}>
					<Paper className={classes.paper}>
						<Typography
							style={{ marginBottom: "1rem" }}
							component="h3"
							variant="h6"
							align="center"
						>
							Entries
						</Typography>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								position: 'absolute',
								right: ".5rem",
								top: ".5rem"
							}}
						>
							<Hidden smDown>
								{entriesView === "grid" ? (
									<Tooltip
										title="View entries as a list"
										aria-label="View entries as a list"
										placement="top"
									>
										<IconButton
											aria-label="View the entries in a list format"
											onClick={() => setEntriesView("list")}
											variant="contained"
										>
											<ViewListIcon />
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip
										title="View entries as a grid"
										aria-label="View entries as a grid"
										placement="top"
									>
										<IconButton
											aria-label="View the entries in a grid format"
											onClick={() => setEntriesView("grid")}
											variant="contained"
										>
											<ViewGridIcon />
										</IconButton>
									</Tooltip>
								)}
							</Hidden>
						</div>

						<Hidden smDown>
							{entriesView === "list" ? (
								<EntriesList entries={entries} />
							) : (
								<EntriesGrid entries={entries} />
							)}
						</Hidden>

						{/*On mobile devices, only allow displaying the EntriesGrid*/}
						<Hidden mdUp>
							<EntriesGrid entries={entries} />
						</Hidden>
					</Paper>
				</Fade>
			)}
		</>
	);
}

//----------
// GRID VIEW
//----------

function EntriesGrid({ entries }) {
	const classes = useStyles();
	const { selectedSite } = useContext(SiteContext);

	if (!entries) {
		return "Please pass entries to the EntriesGrid component...";
	}

	return (
		<Grid container spacing={3}>
			{entries.map((entry, index) => {
				return (
					<Grid item key={index} xs={12} sm={4} md={3} lg={2}>
						<Card elevation={0} className={classes.card} aria-label>
							<Link
								className={classes.link}
								to={`/${selectedSite.slug}/entries/${entry.slug}`}
							>
								<CardActionArea>
									<CardMedia
										className={classes.media}
										image={
											entry.featuredImage
												? `${process.env.REACT_APP_FILE_URL}/${entry.featuredImage.thumbnailName}`
												: placeholder
										}
										title={entry.title}
									/>
									<CardContent className={classes.unitContent}>
										<Typography align="center" component="h4">
											{entry.title}
										</Typography>
									</CardContent>
								</CardActionArea>
							</Link>
						</Card>
					</Grid>
				);
			})}
		</Grid>
	);
}

//----------
// LIST VIEW
//----------
//
function EntriesList({ entries }) {
	const classes = useStyles();
	const { selectedSite } = useContext(SiteContext);

	if (!entries) {
		return "Please pass entries to the EntriesList component...";
	}

	return (
		<>
			{entries.map((entry, index) => {
				return (
					<Card key={index} className={classes.horizCard}>
						<Link
							className={classes.link}
							to={`/${selectedSite.slug}/entries/${entry.slug}`}
						>
							<CardActionArea className={classes.horizCardActionArea}>
								<CardMedia
									className={classes.horizMedia}
									image={
										entry.featuredImage
											? `${process.env.REACT_APP_FILE_URL}/${entry.featuredImage.thumbnailName}`
											: placeholder
									}
									title={entry.title}
								/>
								<CardContent className={classes.horizCardContent}>
									<Typography variant="h6" component="h4">
										{entry.title}
									</Typography>
									<Typography
										component="p"
										className={classes.entryDescription}
										dangerouslySetInnerHTML={{ __html: entry.description }}
									></Typography>
								</CardContent>
							</CardActionArea>
						</Link>
					</Card>
				);
			})}
		</>
	);
}

export default Entries;
